import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaMapMarkerAlt, FaUser } from "react-icons/fa";
import { useEffect, useMemo, useRef, useState } from "react";

export type DestinationOption = {
  key: string;
  label: string;
  /** When true, option is styled as the order customer hop. */
  isCustomer?: boolean;
};

type SearchableDestinationSelectProps = {
  valueKey: string;
  displayLabel?: string;
  options: DestinationOption[];
  placeholder?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  onChange: (key: string) => void;
};

/**
 * Destination dropdown with search inside the open menu.
 * Destination uses map/user icons (From card in the modal keeps the store icon).
 */
export default function SearchableDestinationSelect({
  valueKey,
  displayLabel,
  options,
  placeholder = "Select destination",
  isLoading = false,
  isDisabled = false,
  onChange,
}: SearchableDestinationSelectProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen]);

  const selectedOptionLabel = options.find((o) => o.key === valueKey)?.label;
  const buttonLabel =
    displayLabel ||
    selectedOptionLabel ||
    (isLoading ? "Loading..." : placeholder);
  const hasValue = Boolean(valueKey);
  const selectedIsCustomer =
    valueKey.startsWith("CUSTOMER:") ||
    options.find((o) => o.key === valueKey)?.isCustomer === true;
  const TriggerIcon = selectedIsCustomer ? FaUser : FaMapMarkerAlt;

  const triggerBorder = hasValue ? "blue.300" : "gray.200";
  const triggerColor = hasValue ? "blue.900" : "gray.500";

  return (
    <Popover
      isOpen={isDisabled ? false : isOpen}
      onOpen={() => {
        if (!isDisabled) onOpen();
      }}
      onClose={onClose}
      placement="bottom-start"
      matchWidth
      isLazy
    >
      <Box w="100%">
        <PopoverTrigger>
          <Button
            w="100%"
            h="auto"
            minH="52px"
            py={2.5}
            px={3}
            variant="outline"
            justifyContent="space-between"
            alignItems="center"
            fontWeight="normal"
            bg="white"
            color={triggerColor}
            borderWidth="1.5px"
            borderColor={triggerBorder}
            borderRadius="xl"
            boxShadow="none"
            _hover={{
              bg: "gray.50",
              borderColor: hasValue ? "blue.400" : "gray.300",
            }}
            _active={{ bg: "gray.50" }}
            _disabled={{
              bg: "white",
              color: "gray.500",
              borderColor: triggerBorder,
              opacity: 0.75,
              cursor: "not-allowed",
            }}
            rightIcon={
              <ChevronDownIcon
                boxSize={5}
                flexShrink={0}
                color="blue.900"
              />
            }
            isDisabled={isDisabled}
            onClick={(e) => {
              if (isDisabled) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            textAlign="left"
            whiteSpace="normal"
          >
            <HStack spacing={3} flex={1} minW={0} align="center">
              <Box
                w={8}
                h={8}
                borderRadius="full"
                bg="blue.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Icon as={TriggerIcon} color="blue.800" boxSize={3.5} />
              </Box>
              <Text
                flex={1}
                textAlign="left"
                whiteSpace="normal"
                wordBreak="break-word"
                lineHeight="short"
                fontSize="sm"
                fontWeight={hasValue ? "700" : "500"}
                color={isLoading && !valueKey ? "gray.500" : triggerColor}
              >
                {buttonLabel}
              </Text>
            </HStack>
          </Button>
        </PopoverTrigger>
      </Box>
      <Portal>
        <PopoverContent
          w="100%"
          maxW="420px"
          zIndex={1500}
          bg="white"
          color="gray.800"
          borderColor="gray.200"
          borderRadius="xl"
          boxShadow="lg"
        >
          <PopoverBody p={2} bg="white">
            <Input
              ref={searchRef}
              size="sm"
              mb={2}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg="white"
              color="gray.800"
              borderColor="gray.200"
              borderRadius="lg"
              _placeholder={{ color: "gray.400" }}
            />
            <Box maxH="220px" overflowY="auto">
              <VStack align="stretch" spacing={0}>
                {isLoading && filtered.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" px={2} py={2}>
                    Loading destinations...
                  </Text>
                ) : filtered.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" px={2} py={2}>
                    No matches
                  </Text>
                ) : (
                  filtered.map((opt) => {
                    const isCustomer =
                      opt.isCustomer === true ||
                      opt.key.startsWith("CUSTOMER:");
                    const isSelected = opt.key === valueKey;
                    return (
                      <Box
                        key={opt.key}
                        as="button"
                        type="button"
                        textAlign="left"
                        px={2}
                        py={2}
                        borderRadius="md"
                        bg={isSelected ? "blue.50" : "transparent"}
                        borderWidth={isCustomer ? "1px" : "0"}
                        borderColor={isCustomer ? "blue.100" : "transparent"}
                        mb={isCustomer ? 1 : 0}
                        _hover={{
                          bg: "blue.50",
                        }}
                        onClick={() => {
                          onChange(opt.key);
                          onClose();
                        }}
                      >
                        <HStack
                          justify="space-between"
                          align="center"
                          spacing={2}
                        >
                          <HStack spacing={2} minW={0} flex={1}>
                            <Box
                              w={6}
                              h={6}
                              borderRadius="full"
                              bg="blue.50"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              <Icon
                                as={isCustomer ? FaUser : FaMapMarkerAlt}
                                color="blue.800"
                                boxSize={2.5}
                              />
                            </Box>
                            <Text
                              fontSize="sm"
                              whiteSpace="normal"
                              wordBreak="break-word"
                              fontWeight={
                                isCustomer || isSelected ? "600" : "normal"
                              }
                              color="blue.900"
                            >
                              {opt.label}
                            </Text>
                          </HStack>
                          {isCustomer && (
                            <Badge
                              colorScheme="blue"
                              fontSize="0.65rem"
                              flexShrink={0}
                            >
                              Customer
                            </Badge>
                          )}
                        </HStack>
                      </Box>
                    );
                  })
                )}
              </VStack>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
